import { findCategoryByTitle } from '@eventbrite/categories';
import {
    SegmentedCarousel,
    ToggleChipProps,
    useSegmentedCarouselController,
} from '@eventbrite/collection-carousel';
import { track } from '@eventbrite/datalayer-library';
import { gettext } from '@eventbrite/i18n';
import { Typography } from '@eventbrite/marmalade';
import { logEvent } from '@eventbrite/statsig';
import classNames from 'classnames';
import React, { useEffect, useState } from 'react';
import { STATSIG_EVENT_NAMES } from '../../../experimentation';
import { OtherEventsYouMayLikeContentProps } from '../types';
import { getEventCardsMap } from './getEventCardsMap';
import styles from './OtherEventsYouMayLike.module.scss';
import {
    useOtherEventsYouMayLike,
    useOtherEventsYouMayLikeByCategory,
    useOtherEventsYouMayLikeByLocation,
} from './OtherEventsYouMayLikeData';
import './SegmentedCarousel.scss';
export type OtherEventsYouMayLikeProps = OtherEventsYouMayLikeContentProps;

const TOGGLE_CHIPS = {
    RELATED: 'related',
    CATEGORY: 'category',
    LOCATION: 'location',
} as const;

const TOGGLE_CHIPS_TRACKING_LABELS = {
    RELATED: 'Related events',
    CATEGORY: 'Popular category',
    LOCATION: 'Popular location',
} as const;

const CAROUSEL_HEADING = gettext('Other events you may like');
const header = (
    <Typography color="neutral-900" variant="heading-sm">
        {CAROUSEL_HEADING.toString()}
    </Typography>
);

const calculateTopOffset = () => {
    let isMobile = false;
    if (typeof window !== 'undefined') {
        isMobile = window?.innerWidth <= 960;
    }
    return isMobile;
};

const getAvailableChips = ({
    hasCategoryEvents,
    eventCategoryName,
    hasLocationEvents,
}: {
    hasCategoryEvents: boolean;
    eventCategoryName: string;
    hasLocationEvents: boolean;
}) => {
    const toggleChips: Array<ToggleChipProps> = [
        {
            id: TOGGLE_CHIPS.RELATED,
            label: gettext('Events for you'),
            trackingLabel: TOGGLE_CHIPS_TRACKING_LABELS.RELATED,
            isSelected: true,
        },
    ];

    hasCategoryEvents &&
        toggleChips.push({
            id: TOGGLE_CHIPS.CATEGORY,
            label: gettext('Popular in %(eventCategoryName)s', {
                eventCategoryName,
            }),
            trackingLabel: TOGGLE_CHIPS_TRACKING_LABELS.CATEGORY,
            isSelected: false,
        });

    hasLocationEvents &&
        toggleChips.push({
            id: TOGGLE_CHIPS.LOCATION,
            label: gettext('Popular nearby'),
            trackingLabel: TOGGLE_CHIPS_TRACKING_LABELS.LOCATION,
            isSelected: false,
        });

    return toggleChips;
};

export function OtherEventsYouMayLikeSegmentedCarousel({
    eventId,
    eventCategory,
    shareComponentProps,
    isAuthenticated,
    areMetricsEnabled,
    eventsToRemove,
    locale,
    placeId,
    showToggleChips,
}: OtherEventsYouMayLikeProps & { showToggleChips: boolean }) {
    const isMobile = calculateTopOffset();
    const segmentedCarouselControllerProps = useSegmentedCarouselController();
    const relatedEventsWrapperClasses = classNames(
        styles.relatedEventsWrapper,
        styles.relatedEventsWrapperCarouselAdjust,
    );
    const [events, setEvents] = useState<JSX.Element[]>([]);

    // Get related events
    const relatedEvents = getEventCardsMap({
        events: useOtherEventsYouMayLike({
            eventId,
            isAuthenticated,
            suspense: true,
            eventsToRemove,
        }),
        trackingLabel: TOGGLE_CHIPS_TRACKING_LABELS.RELATED,
        isAuthenticated,
        shareComponentProps,
        areMetricsEnabled,
        locale,
        showToggleChips,
        bucketLabel: CAROUSEL_HEADING.toString(),
    });

    // Get popular events by category
    let category: string | null = null;
    if (eventCategory) {
        // first get category
        category = findCategoryByTitle(eventCategory);
    }

    const categoryEvents = getEventCardsMap({
        events: useOtherEventsYouMayLikeByCategory({
            category: category || null,
            placeId: placeId || null,
            disabled: !showToggleChips,
            suspense: true,
        }),
        trackingLabel: TOGGLE_CHIPS_TRACKING_LABELS.CATEGORY,
        isAuthenticated,
        shareComponentProps,
        areMetricsEnabled,
        locale,
        showToggleChips,
        bucketLabel: CAROUSEL_HEADING.toString(),
    });

    // Get popular events by location
    const locationEvents = getEventCardsMap({
        events: useOtherEventsYouMayLikeByLocation({
            placeId: placeId || null,
            disabled: !showToggleChips,
            suspense: true,
        }),
        trackingLabel: TOGGLE_CHIPS_TRACKING_LABELS.LOCATION,
        isAuthenticated,
        shareComponentProps,
        areMetricsEnabled,
        locale,
        showToggleChips,
        bucketLabel: CAROUSEL_HEADING.toString(),
    });

    const chips = getAvailableChips({
        hasCategoryEvents: categoryEvents.length > 0,
        eventCategoryName: eventCategory || '',
        hasLocationEvents: locationEvents.length > 0,
    });

    const handleSelected = (selectedChip: ToggleChipProps) => {
        logEvent(STATSIG_EVENT_NAMES.LISTING_RELATED_EVENT_PILL_CLICK, null, {
            clickEventType: selectedChip?.trackingLabel ?? '',
        });

        track({
            eventName: STATSIG_EVENT_NAMES.LISTING_RELATED_EVENT_PILL_CLICK,
            eventData: {
                clickEventType: selectedChip?.trackingLabel ?? '',
            },
        });

        if (selectedChip.id === TOGGLE_CHIPS.RELATED) {
            setEvents(relatedEvents);
        } else if (selectedChip.id === TOGGLE_CHIPS.CATEGORY) {
            setEvents(categoryEvents);
        } else if (selectedChip.id === TOGGLE_CHIPS.LOCATION) {
            setEvents(locationEvents);
        }

        segmentedCarouselControllerProps.setShouldUpdateCarousel(true);
    };

    // Initially set events to Related Events
    useEffect(() => {
        if (relatedEvents.length && !events.length) {
            setEvents(relatedEvents);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className={relatedEventsWrapperClasses}>
            <div className={styles.segmentedCarousel}>
                <SegmentedCarousel
                    {...{
                        ...segmentedCarouselControllerProps,
                        hideOverflow: !isMobile,
                        rootPaddingTop: 0,
                        rootPaddingBottom: 0,
                        rootPaddingLeft: isMobile ? 20 : 0,
                        rootPaddingRight: isMobile ? 20 : 0,
                        headerGap: 0,
                    }}
                >
                    <SegmentedCarousel.Header>
                        {showToggleChips ? (
                            <SegmentedCarousel.ToggleChips
                                header={header}
                                toggleChips={chips}
                                onSelected={handleSelected}
                            />
                        ) : (
                            header
                        )}
                    </SegmentedCarousel.Header>
                    <SegmentedCarousel.Track>{events}</SegmentedCarousel.Track>
                    <SegmentedCarousel.Footer />
                </SegmentedCarousel>
            </div>
        </div>
    );
}
